import React, {useCallback, useEffect, useRef, useState} from 'react';
import OutstandingARComponent from './outstanding-ar/OutstandingARComponent';
import RCMpaymentsComponent from './payments/RCMpaymentsComponent';
import RCMDenialsComponent from './denials/RCMDenialsComponent';
import RCMReSubmissionsComponent from './resubmissions/RCMReSubmissionsComponent';
import RCMCollectionsComponent from './collections/RCMCollectionsComponent';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import theme from 'src/styles/theme';
import RCMKPIComponent from './kpi/RCMKPIComponent';
import moment from 'moment';
import EncryptUtils from 'src/utils/EncryptUtils';
import dayjs from 'dayjs';
import {
  exportDashboardReport,
  fetchRCMDashboardData
} from '@shared/services/service-handlers/PrivateService';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import {PulseLoader} from 'react-spinners';
import {handleDownloadPDF_HTML2Canvas} from 'src/utils/pdf-utils';
import ExportAsSelector from '@components/rcm-components/ExportAsSelector';

const RCMDashboardComponent = ({
  data,
  dashboard_type,
  uiFieldsData,
  ...props
}: any) => {
  const [dashboardData, setDashboardData] = useState({});
  const [dashboardType, setDashboardType] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [isPDFExporting, setIsPDFExporting] = useState<boolean>(false);
  const [isExcelExporting, setIsExcelExporting] = useState<boolean>(false);

  useEffect(() => {
    if (dashboard_type?.length) {
      setDashboardType(dashboard_type);
    }
  }, [dashboard_type]);

  useEffect(() => {
    if (data) {
      setDashboardData(data);
    }
    if (JSON.stringify(dashboardData) !== JSON.stringify(data)) {
      setRefreshKey(prev => prev + 1);
    }
  }, [data]);

  // New Content, need to refactor

  const [chartData, setChartData] = useState<any>([]);
  const pdfRef = useRef(null); // Reference to capture the component
  const [loading, setLoading] = useState<boolean>(false);

  const fetchChartData = async () => {
    try {
      setLoading(true);

      if (
        !(data.from_date && data.to_date) ||
        (dashboard_type === 'payments' && !data.financial_class?.length) ||
        (dashboard_type === 'collections' && !data.agency?.length)
      ) {
        return;
      }

      const dashboardName = dashboard_type.toLowerCase().replace(' ', '_');
      // Pass Filter criteria and dashboard type
      const tempData: any = {
        sql_data: uiFieldsData?.dashboard_sql,
        data
      };
      const encryptRequestData = EncryptUtils.encryptedData(tempData);
      const dataObj: any = await fetchRCMDashboardData(
        {tk: encryptRequestData},
        dashboardName
      );
      if (dashboard_type === 'payments')
        dataObj.bar_chart_01.xaxis.categories.splice(0, 1);
      setChartData(dataObj);
    } catch (e) {
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setChartData([]);
    fetchChartData();
  }, [refreshKey]);

  /** Function to trigger PDF download for the active component */
  const handleDownloadPDF = useCallback(() => {
    setIsPDFExporting(true);
    handleDownloadPDF_HTML2Canvas(pdfRef, dashboard_type + '_chart').then(
      (isSuccessful: boolean) => {
        setIsPDFExporting(false);
      }
    );
  }, [dashboard_type]);

  const renderDashboard = useCallback(() => {
    if (!chartData?.table_data_01?.rows?.length) return null;
    switch (dashboardType.toLowerCase()) {
      case 'outstanding_ar':
        return <OutstandingARComponent data={data} chartData={chartData} />;
      case 'payments':
        return <RCMpaymentsComponent data={data} chartData={chartData} />;
      case 'denials':
        return <RCMDenialsComponent data={data} chartData={chartData} />;
      case 'claim_submissions':
        return <RCMReSubmissionsComponent data={data} chartData={chartData} />;
      case 'kpi':
        return <RCMKPIComponent data={data} chartData={chartData} />;
      case 'collections':
        return <RCMCollectionsComponent data={data} chartData={chartData} />;
      default:
        return null;
    }
  }, [dashboardData, refreshKey, chartData, dashboard_type]);

  const getFormattedDate = (date: any) => {
    return date ? dayjs(date).startOf('day').format('YYYY-MM-DD HH:mm:ss') : '';
  };

  const exportPaymentReconciliationReport = async (exportType: string) => {
    const tempData: any = {
      sql_data: uiFieldsData?.dashboard_sql,
      dashboard_type,
      data,
      exportType
    };
    const tk = EncryptUtils.encryptedData(tempData);
    try {
      setIsExcelExporting(true);
      const reconciliationReportData = await exportDashboardReport({tk});
      const fileExtension = exportType === 'csv' ? 'csv' : 'xlsx';
      const filename = `${dashboard_type?.toUpperCase()} Summary ${moment().format('YYYY-MM-DD hh:mm:ss')}.${fileExtension}`;
      const url = window.URL.createObjectURL(
        new Blob([reconciliationReportData.data])
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // setExportLoading(false);
    } finally {
      setIsExcelExporting(false);
    }
  };

  const renderRefreshButton = () => {
    return (
      <LoadingButton
        type="reset"
        variant="outlined"
        onClick={() => setRefreshKey(prev => prev + 1)}
        loading={false}
        // disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.light
        }}>
        Refresh
      </LoadingButton>
    );
  };

  return (
    <Stack>
      <Box sx={{display: 'flex', justifyContent: 'end', mt: 2}}>
        {renderRefreshButton()}
        <ExportAsSelector
          exportAsExcel={exportPaymentReconciliationReport}
          exportAsPDF={handleDownloadPDF}
          disabled={!chartData?.table_data_01?.rows?.length}
          loading={isExcelExporting || isPDFExporting}
        />
      </Box>
      <Stack sx={{m: 3}}>
        <Grid ref={pdfRef}>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: 2, mb: 1}}>
            <CardHeader
              sx={{
                '& .MuiCardHeader-title': {fontSize: '20px !important'}
              }}
              title={String(
                Object.values(uiFieldsData?.dashboard_titles)?.[0] ?? ''
              )}
            />
            <CardContent>
              {loading || !chartData?.table_data_01?.rows?.length ? (
                <Box sx={{m: 5, textAlign: 'center'}}>
                  {loading ? (
                    <CircularProgressDiv height="200px">
                      <PulseLoader color={theme.palette.primary.main} />
                    </CircularProgressDiv>
                  ) : (
                    <Typography>
                      {chartData?.table_data_01?.rows &&
                      !chartData?.table_data_01?.rows?.length
                        ? 'No Data in the selected criteria'
                        : 'Please Select the Filter Criteria to View Data'}
                    </Typography>
                  )}
                </Box>
              ) : (
                renderDashboard()
              )}
            </CardContent>
          </Card>
        </Grid>
      </Stack>
    </Stack>
  );
};

export default RCMDashboardComponent;
